import React, { FC, useState, useEffect } from 'react';
import styles from './TableOptions.module.scss';
import { Modal } from 'antd';
import HiddenColumns from './HiddenColumns';
import Interaction from './Interaction';
import VisibleColumns from './VisibleColumns';
import { saveUnselectedColumns, saveColumnsOrder } from './Helpers';
import { SimpleColumn, ShowIcons } from './types';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { motion } from 'framer-motion';
import IconsContainer from './Icons/IconsContainer';

type SavedKeys = {
  SAVED_HIDDEN_COLUMNS_KEY: string;
  SAVED_COLUMNS_ORDER_KEY: string;
};

type TableOptionsProps = {
  columns: any[];
  tableStatus?: number | undefined;
  onSetStatusTable?: (status: number) => void;
  onColumnsSelected: (toBeHidden: string[]) => void;
  iconsConfig: ShowIcons;
  savedKeys: SavedKeys;
};

const TableOptions: FC<TableOptionsProps> = ({
  columns,
  tableStatus = 0,
  onSetStatusTable = () => {},
  onColumnsSelected,
  iconsConfig,
  savedKeys
}) => {
  const [showTableOptions, setShowModalTable] = useState<boolean>(false);
  const [selectedColumns, setSelectedColumns] = useState<SimpleColumn[]>([]);
  const [toShowColumns, setToShowColumns] = useState<string[]>([]);
  const [toHideColumns, setToHideColumns] = useState<string[]>([]);

  useEffect(() => {
    if (showTableOptions) {
      setSelectedColumns(columns.filter(x => x.isVisible).map(x => ({ key: x.key, title: x.title })));
    }

    return () => {
      setToHideColumns([]);
      setToShowColumns([]);
    };
  }, [columns, showTableOptions]);

  const { SAVED_HIDDEN_COLUMNS_KEY, SAVED_COLUMNS_ORDER_KEY } = savedKeys;

  const toShowColumnsSelected = (checkedValues: CheckboxValueType[]) => {
    setToShowColumns(checkedValues as string[]);
  };

  const toHideColumnsSelected = (checkedValues: CheckboxValueType[]) => {
    setToHideColumns(checkedValues as string[]);
  };

  const onOk = () => {
    setShowModalTable(false);
    saveColumnsOrder(
      selectedColumns.map(x => x.key),
      SAVED_COLUMNS_ORDER_KEY
    );
    const difference = columns.map(x => x.key).filter(x => !selectedColumns.map(y => y.key).includes(x));
    saveUnselectedColumns(difference, SAVED_HIDDEN_COLUMNS_KEY);
    onColumnsSelected(difference);
  };

  const moveHiddenColumnsToShown = () => {
    const columnsToShow = columns.filter(x => toShowColumns.includes(x.key)).map(x => ({ key: x.key, title: x.title }));
    setSelectedColumns([...selectedColumns, ...columnsToShow]);
    setToHideColumns([]);
    setToShowColumns([]);
  };

  const moveShownColumnsToHide = () => {
    const filteredSelectedColumns = selectedColumns.filter(x => !toHideColumns.includes(x.key));
    setSelectedColumns(filteredSelectedColumns);
    setToHideColumns([]);
    setToShowColumns([]);
  };

  const hiddenColumns = columns.map(x => ({ key: x.key, title: x.title })).filter(x => !selectedColumns.map(y => y.key).includes(x.key));
  const anyHidden = hiddenColumns.length > 0;
  const anyShown = selectedColumns.length > 0;

  return (
    <div className={styles.actionRow}>
      <IconsContainer tableStatus={tableStatus} setShowModalTable={setShowModalTable} onSetStatusTable={onSetStatusTable} iconsConfig={iconsConfig} />
      <Modal
        className='selectColumns'
        title='Select Visible Columns'
        centered
        visible={showTableOptions}
        okButtonProps={{ disabled: !anyShown }}
        onOk={onOk}
        onCancel={() => setShowModalTable(false)}
      >
        <motion.div layout style={{ display: 'flex' }}>
          <HiddenColumns
            anyHidden={anyHidden}
            hiddenColumns={hiddenColumns}
            toShowColumns={toShowColumns}
            toShowColumnsSelected={toShowColumnsSelected}
          />
          <Interaction moveHiddenColumnsToShown={moveHiddenColumnsToShown} moveShownColumnsToHide={moveShownColumnsToHide} />
          <VisibleColumns
            anyShown={anyShown}
            selectedColumns={selectedColumns}
            toHideColumns={toHideColumns}
            toHideColumnsSelected={toHideColumnsSelected}
            setSelectedColumns={setSelectedColumns}
          />
        </motion.div>
      </Modal>
    </div>
  );
};

export default TableOptions;
